import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { gnf } from '@/lib/invoices';
import { DUE_BADGE, PLAN_TYPE_LABELS, dueState, fmtDay, fmtKm, nextDue } from '@/lib/maintenance';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { QueryError } from '@/components/QueryError';
import { WO_STATUS_LABELS, WO_STATUS_VARIANT } from '@/components/maintenance/WorkOrderDetailDialog';

import { SummaryTile } from './SummaryTile';

import type { MaintenancePlan, WorkOrder } from '@logistech/ui-shared';

/** GET /maintenance/vehicle-summary/:vehicleId (maintenance-service) */
export interface VehicleMaintenanceSummary {
  nextDueDate: string | null;
  nextDueKmRemaining: number | null;
  workOrdersLast12Months: number;
  costLast12MonthsGnf: number;
}

/** GET /maintenance/plans et /work-orders : limit/offset, pas le Paginated<T> page/pageSize (TD-034) */
export interface VehiclePaginatedPlans { data: MaintenancePlan[]; total: number; limit: number; offset: number }
export interface VehiclePaginatedWorkOrders { data: WorkOrder[]; total: number; limit: number; offset: number }

const kmRemainingLabel = (km: number) =>
  km < 0 ? `dépassée de ${fmtKm(-km)}` : `dans ${fmtKm(km)}`;

export function VehicleMaintenanceTab({ vehicleId }: { vehicleId: string }) {
  const summaryQuery = useQuery({
    queryKey: ['vehicle-maintenance-summary', vehicleId],
    queryFn: async () => (await api.get<VehicleMaintenanceSummary>(`/maintenance/vehicle-summary/${vehicleId}`)).data,
  });
  const plansQuery = useQuery({
    queryKey: ['maintenance-plans', { vehicleId, active: true }],
    queryFn: async () => (await api.get<VehiclePaginatedPlans>(`/maintenance/plans?vehicleId=${vehicleId}&active=true`)).data,
  });
  const ordersQuery = useQuery({
    queryKey: ['work-orders', { vehicleId, limit: 10 }],
    queryFn: async () => (await api.get<VehiclePaginatedWorkOrders>(`/work-orders?vehicleId=${vehicleId}&limit=10`)).data,
  });
  const summary = summaryQuery.data;

  // Échéance date et/ou km : la tuile montre la date, les km restants en sous-titre (ou en valeur s'il n'y a que ça)
  const dueValue = summary?.nextDueDate
    ? fmtDay(summary.nextDueDate)!
    : summary?.nextDueKmRemaining != null ? kmRemainingLabel(summary.nextDueKmRemaining) : 'Aucune';
  const dueSub = summary?.nextDueDate && summary.nextDueKmRemaining != null
    ? kmRemainingLabel(summary.nextDueKmRemaining)
    : summary?.nextDueDate || summary?.nextDueKmRemaining != null ? undefined : 'aucun plan actif avec échéance';

  return (
    <div className="space-y-4">
      <QueryError query={summaryQuery} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryTile label="Prochaine échéance" value={dueValue} sub={dueSub} loading={summaryQuery.isLoading} />
        <SummaryTile label="Ordres (12 mois)" value={String(summary?.workOrdersLast12Months ?? 0)} sub="terminés" loading={summaryQuery.isLoading} />
        <SummaryTile label="Coût (12 mois)" value={gnf(summary?.costLast12MonthsGnf ?? 0)} sub="ordres terminés, GNF" loading={summaryQuery.isLoading} />
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Plans actifs</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <QueryError query={plansQuery} />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Plan</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Prochaine échéance</TableHead>
                <TableHead>État</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {plansQuery.isLoading && (
                <TableRow>{Array.from({ length: 4 }).map((_, j) => <TableCell key={j}><Skeleton className="h-4" /></TableCell>)}</TableRow>
              )}
              {plansQuery.data?.data.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell>{PLAN_TYPE_LABELS[p.type] ?? p.type}</TableCell>
                  <TableCell>{nextDue(p)}</TableCell>
                  <TableCell><Badge variant={DUE_BADGE[dueState(p)].variant}>{DUE_BADGE[dueState(p)].label}</Badge></TableCell>
                </TableRow>
              ))}
              {plansQuery.data && plansQuery.data.data.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">Aucun plan actif</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Ordres récents</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <QueryError query={ordersQuery} />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Référence</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Coût</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ordersQuery.isLoading && (
                <TableRow>{Array.from({ length: 5 }).map((_, j) => <TableCell key={j}><Skeleton className="h-4" /></TableCell>)}</TableRow>
              )}
              {ordersQuery.data?.data.map((o) => (
                <TableRow key={o.id}>
                  <TableCell className="font-mono">{o.reference}</TableCell>
                  <TableCell>{o.plan?.name ?? '—'}</TableCell>
                  <TableCell>{fmtDay(o.completedAt) ?? fmtDay(o.scheduledAt) ?? fmtDay(o.createdAt)}</TableCell>
                  <TableCell><Badge variant={WO_STATUS_VARIANT[o.status]}>{WO_STATUS_LABELS[o.status] ?? o.status}</Badge></TableCell>
                  <TableCell className="text-right tabular-nums">{o.totalCost != null ? gnf(o.totalCost, o.currency) : '—'}</TableCell>
                </TableRow>
              ))}
              {ordersQuery.data && ordersQuery.data.data.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">Aucun ordre de travail</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
